import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface TestResult {
  Id: string;
  TestRunId: string;
  CreatedAt?: number;
  LastUpdatedAt?: number;
  Name: string;
  Description: string;
  Status: TestStatus;
  Reason?: string;
  ExpectedOutput?: Record<string, any>;
  ActualOutput?: Record<string, any>;
  CategoryId: string;
  Category?: any;
}

export enum TestStatus {
  Pending = "Pending",
  ManualAction = "ManualAction",
  Passed = "Passed",
  Failed = "Failed",
}

@Injectable({
  providedIn: 'root'
})
export class TestResultService {
  private api: AxiosInstance;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private authService: KeycloakAuthService, private apiConfig: ApiConfigService) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to dynamically set baseURL and include OAuth token
    this.api.interceptors.request.use((config) => {
      // Dynamically construct baseURL on each request to get current tenantId
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}/testing`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async getResults(): Promise<Array<TestResult> | null> {
    try {
      const response = await this.api.get("/result");
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getResult(id: string): Promise<TestResult | null> {
    try {
      const response = await this.api.get("/result/" + id);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateResult(testResult: TestResult): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.put("/result/" + testResult.Id, testResult);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async createResult(testResult: TestResult): Promise<string | null> {
    try {
      const response = await this.api.post("/result", testResult);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async deleteResult(id: string): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.delete("/result/" + id);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
